import type { BadgeColor } from 'app/components/ui/badge'
import type { SmsAgentOpsCounts, TakeoverCause } from 'app/types/sms-agent-ops'
import { ratesFor } from './smsAgentOpsMetrics'

export const TAKEOVER_CAUSES: TakeoverCause[] = [`staffReply`, `manualToggle`, `optOut`]

export const TAKEOVER_CAUSE_LABEL: Record<TakeoverCause, string> = {
  staffReply: `Staff Replied`,
  manualToggle: `Turned Off`,
  optOut: `Patient Opted Out`,
}

export const TAKEOVER_CAUSE_COLOR: Record<TakeoverCause, BadgeColor> = {
  staffReply: `green`,
  manualToggle: `amber`,
  optOut: `red`,
}

export const TAKEOVER_CAUSE_COUNT_KEY: Record<TakeoverCause, keyof SmsAgentOpsCounts> = {
  staffReply: `staffReplyTakeovers`,
  manualToggle: `manualToggleTakeovers`,
  optOut: `optOutTakeovers`,
}

/** The slice of a Thread change Event that tells a takeover's cause. */
type ThreadChangeEvent = { source?: string | null; data?: unknown }

type FieldChange = { from?: unknown; to?: unknown }

/**
 * Why a thread left the AI agent, or undefined when the Event did not turn it off
 * (plan D8). The API tags the cause as a third `source` segment (`Type:id:cause`, TRK-6032):
 * `from-reply` (staff takeover), `from-toggle` (inbox toggle), `from-opt-out` (patient STOP).
 * Events written before TRK-6032 have no cause segment, so fall back to the old heuristic:
 * STOP is system-sourced; a staff reply clears the agent brief in the same save and the inbox
 * toggle does not.
 */
export function takeoverCauseFor(event: ThreadChangeEvent): TakeoverCause | undefined {
  const data = (event.data ?? {}) as Record<string, FieldChange | undefined>
  if (String(data.aiEnabled?.to) !== `false`) return undefined
  const cause = event.source?.split(`:`)[2]
  if (cause === `from-reply`) return `staffReply`
  if (cause === `from-toggle`) return `manualToggle`
  if (cause === `from-opt-out`) return `optOut`
  // Legacy events (pre-TRK-6032) carry no cause segment; derive it as before.
  if (!event.source?.startsWith(`User:`)) return `optOut`
  return data.agentBrief ? `staffReply` : `manualToggle`
}

/** Below this many AI conversations a takeover rate is noise, so the row is not rated (plan D10). */
export const LOW_VOLUME_FLOOR = 5

/** Takeover rate bands, in percent (plan D10). */
export const TAKEOVER_CRITICAL_AT = 40
export const TAKEOVER_WARNING_AT = 20

export type TakeoverTier = `critical` | `warning` | `healthy` | `lowVolume`

export const TAKEOVER_TIER_LABEL: Record<TakeoverTier, string> = {
  critical: `Critical`,
  warning: `Warning`,
  healthy: `Healthy`,
  lowVolume: `Low Volume`,
}

export const TAKEOVER_TIER_COLOR: Record<TakeoverTier, BadgeColor> = {
  critical: `red`,
  warning: `amber`,
  healthy: `green`,
  lowVolume: `gray`,
}

export function takeoverTierFor(counts: SmsAgentOpsCounts): TakeoverTier {
  const rate = ratesFor(counts).takeoverRate
  if (counts.aiConversations < LOW_VOLUME_FLOOR || rate === undefined) return `lowVolume`
  if (rate >= TAKEOVER_CRITICAL_AT) return `critical`
  if (rate >= TAKEOVER_WARNING_AT) return `warning`
  return `healthy`
}

/**
 * SMS Agent locations for the takeover leaderboard, worst first (plan D10): rated rows
 * by takeover rate, highest first, then low-volume rows by volume, so a location with one
 * conversation and one takeover never tops the list at 100%.
 */
export function takeoverLeaderboard<T extends SmsAgentOpsCounts>(rows: T[]): T[] {
  const rated = rows.filter((row) => takeoverTierFor(row) !== `lowVolume`)
  const lowVolume = rows.filter((row) => takeoverTierFor(row) === `lowVolume` && row.aiConversations > 0)
  return [
    ...rated.sort((a, b) => ratesFor(b).takeoverRate! - ratesFor(a).takeoverRate!),
    ...lowVolume.sort((a, b) => b.aiConversations - a.aiConversations),
  ]
}
